import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { RESET_ERROR, RESET_SUCCESS } from "../../store/Types/AuthTypes";
import { getSlots, updateSlot, reorderSlots, assignProductsToSlot, removeProductFromSlot, getProductsForSlots } from "../../store/AsyncMethods/SlotMethod";
import SlotConfigCard from "./SlotConfigCard";

export default function SlotManagementMain() {
  const dispatch = useDispatch();
  const { slots, productsForSlots } = useSelector((state) => state.SlotReducer);
  const { success, error, user } = useSelector((state) => state.AuthReducer);

  const [isReordering, setIsReordering] = useState(false);
  const [localSlots, setLocalSlots] = useState([]);

  useEffect(() => {
    if (user) {
      dispatch(getSlots());
      dispatch(getProductsForSlots());
    }
  }, [user, dispatch]);

  useEffect(() => {
    const sortedSlots = [...slots].sort((a, b) => a.position - b.position);
    setLocalSlots(sortedSlots);
  }, [slots]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      dispatch({ type: RESET_SUCCESS });
    }
    if (error) {
      toast.error(error);
      dispatch({ type: RESET_ERROR });
    }
  }, [success, error, dispatch]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(localSlots);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalSlots(items);
  };

  const handleReorderSlots = async () => {
    setIsReordering(true);
    const positions = localSlots.map((slot, index) => ({
      id: slot.id,
      position: index + 1
    }));

    const success = await dispatch(reorderSlots(positions));
    if (success) {
      toast.success("Slots reordered successfully!");
    }
    setIsReordering(false);
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Product Slots Management</h1>
          <p className="text-sm text-gray-500 mt-1">Configure 8 dynamic product slots for your frontpage</p>
        </div>
        <div className="flex gap-2">
          <Button
            label="Reorder Slots"
            icon="pi pi-sort"
            className="p-primary-btn"
            onClick={handleReorderSlots}
            loading={isReordering}
            disabled={isReordering}
          />
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="slots" direction="horizontal">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {localSlots.map((slot, index) => (
                <Draggable key={slot.id} draggableId={slot.id.toString()} index={index}>
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                       <SlotConfigCard
                         slot={slot}
                         products={productsForSlots}
                         onUpdate={(id, data) => dispatch(updateSlot(id, data))}
                         onAssignProducts={(slotId, productIds) => dispatch(assignProductsToSlot(slotId, productIds))}
                         onRemoveProduct={(slotId, productId) => dispatch(removeProductFromSlot(slotId, productId))}
                       />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      {slots.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg">No slots found</div>
          <div className="text-gray-500 text-sm mt-2">Please check your database setup</div>
        </div>
      )}
    </div>
  );
}